import { useI18n } from 'vue-i18n';

import { useToastStore } from '@/stores/toast';
import { PaymentErrorName } from '@/types/components';

export const useHandleErrorPayment = () => {
  const { t } = useI18n();
  const toastStore = useToastStore();

  const TOSS_ERROR_TO_INTERNAL: Record<string, PaymentErrorName> = {
    INVALID_API_KEY: 'INVALID_API_KEY',
    FORBIDDEN_REQUEST: 'FORBIDDEN_REQUEST',
    INVALID_REQUEST: 'INVALID_REQUEST',

    INVALID_CARD_NUMBER: 'INVALID_CARD_NUMBER',
    INVALID_CARD_EXPIRATION: 'INVALID_CARD_EXPIRATION',
    INVALID_CARD_INFO_RE_REGISTER: 'INVALID_CARD_INFO_RE_REGISTER',
    INVALID_STOPPED_CARD: 'INVALID_CARD_INFO_RE_REGISTER',

    INVALID_REJECT_CARD: 'INVALID_REJECT_CARD',

    REJECT_ACCOUNT_PAYMENT: 'REJECT_CARD_PAYMENT',
    REJECT_CARD_PAYMENT: 'REJECT_CARD_PAYMENT',
    EXCEED_MAX_ONE_DAY_AMOUNT: 'REJECT_CARD_PAYMENT',
    EXCEED_MAX_ONE_DAY_WITHDRAW_AMOUNT: 'REJECT_CARD_PAYMENT',

    NOT_FOUND_METHOD: 'NOT_FOUND_METHOD',
    NOT_FOUND_PAYMENT_METHOD: 'NOT_FOUND_METHOD',

    NOT_SUPPORTED_METHOD: 'NOT_SUPPORTED_METHOD',
    NOT_FOUND_CUSTOMER: 'NOT_FOUND_CUSTOMER',

    FAILED_PAYMENT_INTERNAL_SYSTEM_PROCESSING:
      'FAILED_PAYMENT_INTERNAL_SYSTEM_PROCESSING',
    FAILED_INTERNAL_SYSTEM_PROCESSING:
      'FAILED_PAYMENT_INTERNAL_SYSTEM_PROCESSING',

    FAILED_PAYMENT_CONFIRM: 'FAILED_PAYMENT_CONFIRM',

    REJECT_CARD_COMPANY: 'REJECT_CARD_COMPANY',
    FAILED_CARD_COMPANY: 'FAILED_CARD_COMPANY',

    UNKNOWN_PAYMENT_ERROR: 'UNKNOWN_PAYMENT_ERROR',
    DUPLICATED_ORDER_ID: 'DUPLICATED_ORDER_ID',

    ALREADY_CANCELED_PAYMENT: 'ALREADY_CANCELED_PAYMENT',
    ALREADY_REFUND_PAYMENT: 'ALREADY_CANCELED_PAYMENT',

    NOT_CANCELABLE_AMOUNT: 'NOT_CANCELABLE_PAYMENT',
    NOT_CANCELABLE_PAYMENT: 'NOT_CANCELABLE_PAYMENT',

    REFUND_REJECTED: 'REFUND_REJECTED',
    NOT_FOUND_PAYMENT: 'NOT_FOUND_PAYMENT',

    MAINTAINED_METHOD: 'MAINTAINED_METHOD',
    FAILED_SYSTEM_CHECK_TIME: 'FAILED_SYSTEM_CHECK_TIME',

    PROVIDER_ERROR: 'PROVIDER_ERROR',
    UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  };

  const STRIPE_ERROR_TO_INTERNAL: Record<string, PaymentErrorName> = {
    platform_api_key_expired: 'INVALID_API_KEY',
    api_key_expired: 'INVALID_API_KEY',

    capture_unauthorized_payment: 'FORBIDDEN_REQUEST',
    intent_invalid_state: 'INVALID_REQUEST',

    invalid_number: 'INVALID_CARD_NUMBER',
    incorrect_number: 'INVALID_CARD_NUMBER',

    invalid_cvc: 'INVALID_CARD_NUMBER',
    incorrect_cvc: 'INVALID_CARD_NUMBER',

    expired_card: 'INVALID_CARD_EXPIRATION',
    invalid_expiry_month: 'INVALID_CARD_EXPIRATION',
    invalid_expiry_year: 'INVALID_CARD_EXPIRATION',

    invalid_card_type: 'INVALID_CARD_INFO_RE_REGISTER',

    card_declined: 'INVALID_REJECT_CARD',
    card_decline_rate_limit_exceeded: 'INVALID_REJECT_CARD',

    insufficient_funds: 'REJECT_CARD_PAYMENT',
    balance_insufficient: 'REJECT_CARD_PAYMENT',

    payouts_limit_exceeded: 'REJECT_CARD_PAYMENT',
    charge_exceeds_source_limit: 'REJECT_CARD_PAYMENT',
    charge_exceeds_transaction_limit: 'REJECT_CARD_PAYMENT',
    instant_payouts_limit_exceeded: 'REJECT_CARD_PAYMENT',

    payment_method_unactivated: 'NOT_FOUND_METHOD',
    payment_method_unexpected_state: 'NOT_FOUND_METHOD',
    payment_intent_authentication_failure: 'NOT_FOUND_METHOD',

    payment_method_not_available: 'NOT_SUPPORTED_METHOD',

    processing_error: 'FAILED_PAYMENT_INTERNAL_SYSTEM_PROCESSING',

    payment_intent_payment_attempt_failed: 'FAILED_PAYMENT_CONFIRM',
    setup_intent_authentication_failure: 'FAILED_PAYMENT_CONFIRM',

    payment_method_provider_timeout: 'UNKNOWN_PAYMENT_ERROR',

    charge_already_captured: 'DUPLICATED_ORDER_ID',
    charge_already_refunded: 'ALREADY_CANCELED_PAYMENT',

    return_intent_already_processed: 'ALREADY_CANCELED_PAYMENT',

    amount_too_large: 'NOT_CANCELABLE_PAYMENT',
    amount_too_small: 'NOT_CANCELABLE_PAYMENT',

    charge_disputed: 'REFUND_REJECTED',

    resource_missing: 'NOT_FOUND_PAYMENT',
  };

  const CATALOG_ERROR_PAYMENT = {
    20001: { category: 'AUTH', name: 'INVALID_API_KEY' },
    20002: { category: 'AUTH', name: 'FORBIDDEN_REQUEST' },
    20003: { category: 'AUTH', name: 'INVALID_REQUEST' },
    20004: { category: 'AUTH', name: 'INVALID_ACCESS_TOKEN' },

    21001: { category: 'CARD_INFO', name: 'INVALID_CARD_NUMBER' },
    21002: { category: 'CARD_INFO', name: 'INVALID_CARD_EXPIRATION' },
    21003: { category: 'CARD_INFO', name: 'INVALID_CARD_INFO_RE_REGISTER' },
    21004: { category: 'CARD_INFO', name: 'INVALID_REJECT_CARD' },

    22001: { category: 'METHOD_OR_LIMIT', name: 'REJECT_CARD_PAYMENT' },
    22002: { category: 'METHOD_OR_LIMIT', name: 'NOT_FOUND_METHOD' },
    22003: { category: 'METHOD_OR_LIMIT', name: 'NOT_SUPPORTED_METHOD' },
    22004: { category: 'METHOD_OR_LIMIT', name: 'NOT_FOUND_CUSTOMER' },

    23001: {
      category: 'PAYMENT_PROCESSING',
      name: 'FAILED_PAYMENT_INTERNAL_SYSTEM_PROCESSING',
    },
    23002: { category: 'PAYMENT_PROCESSING', name: 'FAILED_PAYMENT_CONFIRM' },
    23003: { category: 'PAYMENT_PROCESSING', name: 'REJECT_CARD_COMPANY' },
    23004: { category: 'PAYMENT_PROCESSING', name: 'FAILED_CARD_COMPANY' },
    23005: { category: 'PAYMENT_PROCESSING', name: 'UNKNOWN_PAYMENT_ERROR' },
    23006: { category: 'PAYMENT_PROCESSING', name: 'DUPLICATED_ORDER_ID' },

    24001: { category: 'CANCEL_OR_REFUND', name: 'ALREADY_CANCELED_PAYMENT' },
    24002: { category: 'CANCEL_OR_REFUND', name: 'NOT_CANCELABLE_PAYMENT' },
    24003: { category: 'CANCEL_OR_REFUND', name: 'REFUND_REJECTED' },
    24004: { category: 'CANCEL_OR_REFUND', name: 'NOT_FOUND_PAYMENT' },

    25001: { category: 'SYSTEM', name: 'MAINTAINED_METHOD' },
    25002: { category: 'SYSTEM', name: 'FAILED_SYSTEM_CHECK_TIME' },
    25003: { category: 'SYSTEM', name: 'PROVIDER_ERROR' },
    25004: { category: 'SYSTEM', name: 'UNKNOWN_ERROR' },
  } as const;

  const handleErrorAddCardToss = (errorCode: string) => {
    const keyError = TOSS_ERROR_TO_INTERNAL[errorCode];
    toastStore.error(t(keyError));
  };

  const handleErrorAddCardStripe = (errorCode: string) => {
    const keyError = STRIPE_ERROR_TO_INTERNAL[errorCode];
    toastStore.error(t(keyError));
  };

  const handleErrorPayment = (errorPayment) => {
    const keyError = CATALOG_ERROR_PAYMENT[errorPayment.errorCode].name;
    toastStore.error(t(keyError));
  };

  return {
    handleErrorAddCardToss,
    handleErrorAddCardStripe,
    handleErrorPayment,
  };
};
